import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Tab,
  TabItem,
  Chip,
  Tooltip,
  Clipboard,
  ProgressBar,
} from 'mw-style-react';
import useIntl from 'useIntl';
import cn from 'classnames';
import AppUtils from '@control-front-end/utils/utils';
import { GET_ACCESS_RULES, NOTIFY_LEVEL, SHOW_NOTIFY } from 'constants';
import {
  CLEAR_FORM,
  CREATE_FORM,
  GET_FORM,
  GET_FORM_ACCOUNTS,
  UPDATE_FORM,
} from '@control-front-end/common/constants/forms';
import FormConstructor from '@control-front-end/formConstructor';
import { getCanEditForm } from '../../selectors';
import { getActiveAccountOwnerId } from '../../../../selectors';
import mes from './intl';
import './CreateForm.scss';

function CreateForm(props) {
  const { onContentChange, onClose, onFormSave, data = {} } = props;
  const t = useIntl();
  const formConstructor = useRef();
  const portal = useRef();
  const dispatch = useDispatch();
  const formView = useSelector((state) => state.formView);
  const auth = useSelector((state) => state.auth);
  const canEdit = useSelector(getCanEditForm);
  const accountOwnerId = useSelector(getActiveAccountOwnerId);
  const [activeTab, setActiveTab] = useState('constructor');
  const [isLoaded, setIsLoaded] = useState(!data.formId);
  const [errorsChecked, setErrorsChecked] = useState(false);
  const [actorsAccess, setActorsAccess] = useState([]);
  const [formAccounts, setFormAccounts] = useState([]);
  // Создать элементы куда будут вставлены поля для редактирования title и description
  const portalTitle = useRef(document.createElement('div'));

  useEffect(() => {
    const { formId } = data;
    if (formId) {
      dispatch({
        type: GET_FORM.REQUEST,
        payload: { formId },
        afterReq: () => setIsLoaded(true),
      });
      dispatch({
        type: GET_ACCESS_RULES.REQUEST,
        payload: {
          objType: 'templateActors',
          objId: formId,
        },
        callback: setActorsAccess,
      });
      dispatch({
        type: GET_FORM_ACCOUNTS.REQUEST,
        payload: { formId },
        callback: setFormAccounts,
      });
    }
    // Динамическая вставка полей редактирования мета данных формы
    portal.current.appendChild(portalTitle.current);

    return () => {
      dispatch({ type: CLEAR_FORM });
    };
  }, []);

  const notifyErrors = (formErrors) => {
    formErrors.forEach((err) => {
      setTimeout(
        () =>
          dispatch({
            type: SHOW_NOTIFY.REQUEST,
            payload: {
              id: AppUtils.createRid(),
              type: NOTIFY_LEVEL.ERROR,
              label: `${err.title || err.id} ${err.key} error: ${err.message}.`,
            },
          }),
        500
      );
    });
  };

  // Получить json формы
  const getFormJson = () => {
    if (!formConstructor.current) return null;
    return formConstructor.current.getForm();
  };

  // Сохранить форму
  const handleFormSave = () => {
    const { formId } = data;
    const formJson = getFormJson();
    setErrorsChecked(true);
    if (formJson.formErrors && formJson.formErrors.length) {
      notifyErrors(formJson.formErrors);
      return;
    }
    formJson.form.sections.forEach((section) => {
      section.content.forEach((item) => {
        if (item.extra && item.extra.optionsSource) {
          const { type } = item.extra.optionsSource;
          if (type && type !== 'manual') {
            item.options = [];
          } else if (type && type === 'manual') {
            delete item.extra.optionsSource.value;
          }
        }
      });
    });
    const actions = {
      onClose,
      setSubmitting: setIsLoaded,
    };
    if (onFormSave) actions.callback = onFormSave;
    dispatch({
      type: formId ? UPDATE_FORM.REQUEST : CREATE_FORM.REQUEST,
      payload: {
        isTemplate: true,
        formId,
        form: formJson.form,
        access: formJson.access,
        actorsAccess: formJson.actorsAccess,
        formAccounts: formJson.formAccounts,
      },
      formActions: actions,
    });
  };

  const renderTabs = () => (
    <Tab
      value={activeTab}
      type="auto"
      onChange={({ value }) => {
        if (isLoaded) setActiveTab(value);
      }}
      underline={true}
    >
      <TabItem label={t(mes.constr)} value="constructor" />
      <TabItem label={t(mes.preview)} value="preview" />
      <TabItem label={t(mes.formAccessRules)} value="access" />
      <TabItem label={t(mes.actorsAccessRules)} value="actorsAccess" />
      <TabItem label={t(mes.actorsDefAccounts)} value="accounts" />
      <TabItem label={t(mes.settings)} value="settings" />
    </Tab>
  );

  const renderIdChip = () => {
    if (!formView.id) return null;
    return (
      <>
        <Tooltip value={t(mes.copyFormId)}>
          <Chip
            styleName="cf__header__actions__chip"
            label={`ID: ${AppUtils.toNumberFormat(formView.id.toString())}`}
            fontWeight="normal"
            icon="copy"
            closeIcon={false}
            type="rectangular"
            onClick={() => {
              Clipboard.copy(formView.id.toString());
              dispatch({
                type: SHOW_NOTIFY.REQUEST,
                payload: {
                  id: AppUtils.createRid(),
                  type: NOTIFY_LEVEL.SUCCESS,
                  label: t(mes.formIdCopied),
                },
              });
            }}
          />
        </Tooltip>
        <div styleName="cf__header__actions__divider" />
      </>
    );
  };

  const renderMainContent = () => (
    <FormConstructor
      key={formView.id}
      action={canEdit ? activeTab : 'preview'}
      formData={formView.form}
      formAccounts={formAccounts}
      access={
        formView.access || [
          {
            avatar: auth.avatar,
            name: auth.nick,
            userId: auth.id,
            privs: {
              view: true,
              modify: true,
              remove: true,
            },
          },
        ]
      }
      actorsAccess={actorsAccess}
      accountOwnerId={accountOwnerId}
      ownerId={formView.ownerId || auth.id}
      canEdit={canEdit}
      isNew={!data.formId}
      isTemplate={true}
      errorsChecked={errorsChecked}
      defaultTitle={data.title}
      titlePortal={portalTitle.current}
      ref={formConstructor}
      onContentChange={onContentChange}
    />
  );

  return (
    <div styleName="cf__wrap">
      <div styleName={cn('cf', { readOnly: !canEdit })}>
        <div styleName="cf__header">
          <div styleName="cf__header__info">
            <Button
              type="tertiary"
              size="smallplus"
              fontWeight="normal"
              rounded={true}
              icon="close"
              visibility={isLoaded ? 'visible' : 'disabled'}
              onClick={onClose}
            />
            <div styleName="cf__header__title" ref={portal} />
          </div>
          <div styleName="cf__header__actions">
            {renderIdChip()}
            {canEdit ? (
              <Button
                size="smallplus"
                fontWeight="normal"
                label={data.formId ? t(mes.update) : t(mes.create)}
                visibility={isLoaded ? 'visible' : 'disabled'}
                onClick={handleFormSave}
              />
            ) : null}
          </div>
        </div>
        {canEdit ? <div styleName="cf__tabs">{renderTabs()}</div> : null}
        {isLoaded ? (
          renderMainContent()
        ) : (
          <div styleName="cf__loader">
            <ProgressBar type="circle" size="large" />
          </div>
        )}
      </div>
    </div>
  );
}

CreateForm.propTypes = {
  data: PropTypes.object,
  onClose: PropTypes.func,
  onFormSave: PropTypes.func,
  onContentChange: PropTypes.func,
};

export default CreateForm;
